<template>
  <a-form
    autocomplete="off"
    :model="model"
    :rules="form.rules"
    :layout="form.settings.layout"
    scrollToFirstError
    class="cards"
    :validateTrigger="false"
  >
    <template
      v-for="(row, index) in form.dynamicFieldList"
      :key="index"
    >
      <a-card
        :body-style="{ padding: '10px 10px 0' }"
        style="margin-bottom: 5px"
      >
        <template
          v-if="row.group_name && (form.dynamicFieldList.length > 1 || alwaysShowGroup)"
          #title
        >
          {{ row.group_name }}
        </template>
        <template #extra>
          <div
            class="group-custom-btn-list"
            v-if="row.custom_btn_list && row.custom_btn_list.length > 0"
          >
            <template v-for="btn in row.custom_btn_list">
              <debug-code
                :action_code_id="btn.action_code_id"
                v-model:code="btn.code"
              >
                <a-button
                  v-if="!lockStructure || btn.show_on_modify"
                  size="small"
                  :type="btn.type"
                  :danger="btn.danger"
                  @click="handleCustomBtnClick(btn)"
                >
                  {{ btn.label }}
                </a-button>
              </debug-code>
            </template>
          </div>
        </template>
        <a-row
          :gutter="5"
          type="flex"
        >
          <template v-if="getColFieldList(row?.children).length > 0">
            <a-col
              v-for="col in getColFieldList(row?.children)"
              :key="col[0]"
              :xl="24 / getColFieldList(row?.children).length"
              :lg="24"
              :md="24"
              :sm="24"
              :xs="24"
            >
              <field-col
                v-for="(field_item, i) in row?.children.slice(col[0], col[1])"
                :key="i"
                :model="model"
                :out-model="outModel"
                :options="options"
                :validate-infos="validateInfos"
                :always-show-group="alwaysShowGroup"
                :field_item="field_item"
                :context="context"
                :inner-model="_innerModel"
                :global-inner-model="_globalInnerModel"
                :nestValidate="nestValidate"
                :base-key-path="baseKeyPath"
                :worker-host-functions="workerHostFunctions"
                @update_model="onUpdateModel"
                @update_name2="onUpdateName2"
              ></field-col>
            </a-col>
          </template>
          <field-col
            v-else
            v-for="(field_item, i) in row?.children"
            :key="i"
            :model="model"
            :out-model="outModel"
            :options="options"
            :validate-infos="validateInfos"
            :always-show-group="alwaysShowGroup"
            :field_item="field_item"
            :context="context"
            :inner-model="_innerModel"
            :global-inner-model="_globalInnerModel"
            :nestValidate="nestValidate"
            :base-key-path="baseKeyPath"
            :worker-host-functions="workerHostFunctions"
            @update_model="onUpdateModel"
            @update_name2="onUpdateName2"
          ></field-col>
        </a-row>
      </a-card>
    </template>
  </a-form>
</template>

<script lang="ts">
import { defineComponent, watch, reactive, PropType, provide, computed } from 'vue';
import FieldCol from './FieldCol.vue';
import { executeScriptInWorker } from 'liyu-pc-base/utils/function';
import { message } from 'ant-design-vue';
import debugCode from 'liyu-pc-base/components/FormRender/debugCode.vue';

export default defineComponent({
  name: 'FormRender3',
  components: {
    FieldCol,
    debugCode,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
    outModel: {
      type: Object,
      default: () => {},
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
      default: () => {},
    },
    validateInfos: {
      type: Object,
      default: () => {},
    },
    alwaysShowGroup: {
      type: Boolean,
      default: false,
    },
    context: {
      type: Object,
      default: null,
    },
    innerModel: {
      type: Object,
      default: null,
    },
    globalInnerModel: {
      default: null,
    },
    nestValidate: {
      type: Object as PropType<Record<string, any> | null>,
      default: null,
    },
    baseKeyPath: {
      type: Array,
      default: () => [],
    },
    workerHostFunctions: {
      type: Object,
      default: () => {},
    },
    // 修改模式传入 true：深层 FormTemplate/FormItem 隐藏系统按钮与行删除（provide 给子组件）
    lockStructure: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update_model'],
  setup(props, { emit }) {
    // 向深层子组件（FieldCol → FormTemplate/FormItem 等）提供 lockStructure，避免逐层透传
    provide('FORM_LOCK_STRUCTURE', computed(() => props.lockStructure));
    // 解决传入非响应式数据时，动态增加is_show_field无用正常相应问题
    if (props.form.watch) {
      for (let k in props.form.watch) {
        watch(
          () => props.model[k],
          () => {
            props.form.watch[k](props.model, props.form);
          },
        );
      }
    }
    const _globalInnerModel = props.globalInnerModel || reactive({});
    const _innerModel = props.innerModel || _globalInnerModel;

    // 支持 form.settings.col 分列（与 FormRender fields 模式一致）：
    // settings.col 数组每项表示该列容纳的字段个数，如 [11, 11, 12] 表示三列，分别放 11/11/12 个字段
    // 返回每列的 [start, end) 区间；未配置 col 或 children 为空时返回 []（保持单列原逻辑）
    const getColFieldList = (children: any[]): Array<[number, number]> => {
      const col = props.form?.settings?.col;
      if (!col || col.length === 0 || !children || children.length === 0) {
        return [];
      }
      const list: Array<[number, number]> = [];
      let a = 0;
      col.forEach((item: number, i: number) => {
        a = a + item;
        if (i === 0) {
          list.push([0, a]);
        } else {
          list.push([a - item, a]);
        }
      });
      return list;
    };

    const validate = () => {
      return (props.form as any).validate();
    };
    const onUpdateModel = params => {
      emit('update_model', params);
    };
    const onUpdateName2 = params => {
      emit('update_model', params);
    };
    const handleCustomBtnClick = async btn => {
      if (btn.code) {
        let res;
        try {
          res = await executeScriptInWorker(
            btn.code,
            {
              context: props.context,
              formModel: props.model,
              keyPath: [...props.baseKeyPath, btn.group_name],
            },
            props.workerHostFunctions || {},
          );
        } catch (e) {
          message.error(e.message);
          return;
        }
        if (res) {
          Object.assign(props.model, res);
        }
      }
    };
    return {
      validate,
      onUpdateModel,
      onUpdateName2,
      _globalInnerModel,
      _innerModel,
      handleCustomBtnClick,
      getColFieldList,
    };
  },
});
</script>

<style lang="less" scoped>
.cards {
  ::v-deep(.ant-card-head) {
    padding: 0 8px;
    min-height: 44px;
    background-color: rgba(0, 0, 0, 0.02);
  }
}
.group-custom-btn-list {
  display: inline-flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-right: 2px;
}
</style>
